/**
 * @copyright
 * Copyright (c) 2024 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import { FedmodBadge } from "@atg/icf-federatedmodule";
import { FC, Suspense } from "react";
import { createUseStyles } from "react-jss";
import { useSelector } from "react-redux";

import cbLogoImg from "~/assets/images/cb_logo.svg";
import CustomLink from "~/components/CustomLink";
import { PAGE_CONTENT_MAX_WIDTH, PAGE_CONTENT_PADDING } from "~/constants";
import { selectAuthAccount } from "~/features/auth/selectors";
import DesktopMenu from "~/features/header/components/DesktopMenu";
import MobileMenu from "~/features/header/components/MobileMenu";
import { FIXED_HEADER_HEIGHT } from "~/features/header/constants";
import useScrollStatus from "~/hooks/useScrollStatus";
import getServerContext from "~/utils/getServerContext";

const useStyles = createUseStyles({
  header: {
    position: "fixed",
    width: "100%",
    top: 0,
    // eslint-disable-next-line @atg/css-rtl-support
    left: 0,
    zIndex: 1000,
    transition: "background-color 0.15s ease",
  },
  container: {
    boxSizing: "border-box",
    height: `${FIXED_HEADER_HEIGHT}px`,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: PAGE_CONTENT_MAX_WIDTH,
    maxWidth: "100%",
    padding: `0 ${PAGE_CONTENT_PADDING}px`,
    margin: "0 auto",
  },
  logo: {
    width: "173px",
  },
});

const FixedHeader: FC = () => {
  const account = useSelector(selectAuthAccount);
  const { scrollHeaderPercent } = useScrollStatus();
  const classes = useStyles();

  const { federatedModules } = getServerContext();
  const logoRedirectLink = account ? "/app/dashboard" : "/";

  return (
    <header className={classes.header} style={{ background: `rgba(0, 0, 0, ${scrollHeaderPercent})` }}>
      <div className={classes.container}>
        <FedmodBadge clientNames={Object.keys(federatedModules)}>
          <CustomLink to={logoRedirectLink}>
            <img className={classes.logo} src={cbLogoImg} alt="Cambuilder Logo" />
          </CustomLink>
        </FedmodBadge>

        <Suspense fallback={null}>
          <DesktopMenu />
        </Suspense>

        <Suspense fallback={null}>
          <MobileMenu />
        </Suspense>
      </div>
    </header>
  );
};

export default FixedHeader;
